import type { BillingSummary, Source } from '../../../types';

/** What the catalogue says about a source under the user's plan. */
export type SourceBillingBadge =
  /** A personal source: free on every plan. */
  | { kind: 'free' }
  /** A professional source on trial. */
  | { kind: 'trial'; endsAt: string }
  /** Connecting it starts a free trial (Free plan, never tried). */
  | { kind: 'trialAvailable' }
  /** The plan doesn't cover another professional source. */
  | { kind: 'upgrade' };

const sameId = (left: string, right: string) => left.toLowerCase() === right.toLowerCase();

/**
 * The badge for a catalogue source, or null when there is nothing to say (the source is already covered,
 * billing is off for the user, or limits aren't enforced in their region yet). Mirrors the server's connect
 * rules; the server still decides.
 */
export function getSourceBillingBadge(source: Source, billing: BillingSummary | undefined): SourceBillingBadge | null {
  if (!billing?.billingEnabled || !billing.enforcementEnabled || !source.tier) return null;
  if (source.tier === 'personal') return { kind: 'free' };

  const inUse = billing.usage?.professionalSourceTypes ?? [];
  if (inUse.some((id) => sameId(id, source.id))) return null;

  const trial = billing.sourceTrials?.find((candidate) => sameId(candidate.sourceTypeId, source.id));
  if (trial?.isActive) return { kind: 'trial', endsAt: trial.endsAt };

  const max = billing.limits?.maxProfessionalSources;
  if (max === undefined || max === null || inUse.length < max) return null;

  if (billing.limits?.trialsOnConnect && !trial) return { kind: 'trialAvailable' };
  return { kind: 'upgrade' };
}
